class SstScratchNewsletterPopup extends HTMLElement {
  connectedCallback() {
    if (this.initialized || this.hasAttribute('hidden')) return;

    this.initialized = true;
    this.sectionId = this.dataset.sectionId || 'default';
    this.delay = Number(this.dataset.delay || 8000);
    this.scrollPercent = Number(this.dataset.scroll || 35);
    this.threshold = Number(this.dataset.threshold || 55);
    this.repeatDays = Number(this.dataset.repeatDays || 7);
    this.reopenAfterSubmit = this.dataset.reopenAfterSubmit === 'true';
    this.toastMessage = this.dataset.toastMessage || 'Merci. Your discount is being added to checkout.';
    this.toastDuration = 4000;
    this.closeKey = `sstScratchPopupClosed:${this.sectionId}`;
    this.submittedKey = `sstScratchPopupSubmitted:${this.sectionId}`;
    this.redeemingKey = `sstScratchPopupRedeeming:${this.sectionId}`;
    this.isScratching = false;
    this.isRevealed = false;

    this.dialog = this.querySelector('.sst-scratch-popup__dialog');
    this.stage = this.querySelector('[data-sst-stage]');
    this.formPanel = this.querySelector('[data-sst-panel="form"]');
    this.scratchPanel = this.querySelector('[data-sst-panel="scratch"]');
    this.form = this.querySelector('[data-sst-scratch-form]');
    this.message = this.querySelector('[data-sst-scratch-message]');
    this.canvas = this.querySelector('[data-sst-scratch-canvas]');
    this.eiffelArt = this.querySelector('[data-sst-eiffel-art]');
    this.card = this.querySelector('[data-sst-scratch-card]');
    this.progress = this.querySelector('[data-sst-scratch-progress]');
    this.copyButton = this.querySelector('[data-sst-copy-code]');
    this.discountCode = this.querySelector('[data-sst-discount-code]');
    this.closeButtons = this.querySelectorAll('[data-sst-scratch-close]');

    this.bindEvents();

    if (
      new URLSearchParams(window.location.search).get('customer_posted') === 'true' &&
      sessionStorage.getItem(this.redeemingKey) === 'true'
    ) {
      localStorage.setItem(this.submittedKey, 'true');
      localStorage.removeItem(this.closeKey);
      sessionStorage.removeItem(this.redeemingKey);
      this.showSubmissionToast();
      this.showScratch();
      return;
    } else {
      this.showScratch();
      this.scheduleOpen();
    }
  }

  bindEvents() {
    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });

    if (this.copyButton) {
      this.copyButton.addEventListener('click', () => this.copyCode());
    }

    if (this.form) {
      this.form.addEventListener('submit', () => {
        sessionStorage.setItem(this.redeemingKey, 'true');
        const submitButton = this.form.querySelector('[type="submit"]');
        if (submitButton) submitButton.setAttribute('disabled', 'disabled');
      });
    }

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail.sectionId === this.sectionId) this.open();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail.sectionId === this.sectionId) this.close(false);
      });
    }
  }

  scheduleOpen() {
    if (window.Shopify?.designMode) return;
    if (!this.reopenAfterSubmit && localStorage.getItem(this.submittedKey)) return;
    if (this.wasClosedRecently()) return;

    window.setTimeout(() => this.open(), this.delay);

    if (this.scrollPercent > 0) {
      this.scrollHandler = () => {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollable <= 0) return;
        const scrolled = (window.scrollY / scrollable) * 100;
        if (scrolled >= this.scrollPercent) {
          window.removeEventListener('scroll', this.scrollHandler);
          this.open();
        }
      };
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }

  wasClosedRecently() {
    if (this.repeatDays === 0) return false;

    const stored = Number(localStorage.getItem(this.closeKey) || 0);
    if (!stored) return false;

    const repeatMs = this.repeatDays * 24 * 60 * 60 * 1000;
    return Date.now() - stored < repeatMs;
  }

  open() {
    if (this.classList.contains('is-open')) return;

    this.classList.add('is-open');
    document.body.classList.add('sst-scratch-popup-open');
    window.setTimeout(() => this.dialog?.focus(), 0);
  }

  close(remember = true) {
    this.classList.remove('is-open');
    document.body.classList.remove('sst-scratch-popup-open');

    if (remember && !window.Shopify?.designMode) {
      localStorage.setItem(this.closeKey, String(Date.now()));
    }
  }

  showScratch() {
    this.classList.remove('is-redeem-step');
    this.stage?.setAttribute('data-sst-stage', 'scratch');
    this.formPanel.hidden = true;
    this.scratchPanel.hidden = false;
    this.dialog?.setAttribute('aria-labelledby', this.scratchPanel.querySelector('.sst-scratch-popup__title')?.id || '');
    this.initCanvas();
  }

  showEmailForm(isSubmitted = false) {
    this.classList.add('is-redeem-step');
    this.stage?.setAttribute('data-sst-stage', 'redeem');
    this.scratchPanel.hidden = false;
    this.formPanel.hidden = false;
    this.formPanel.querySelector('input[type="email"]')?.focus();

    if (isSubmitted && this.message && !this.message.textContent.trim()) {
      this.message.textContent = 'Merci. Your discount is being added to checkout.';
    }
  }

  showSubmissionToast() {
    const existingToast = document.querySelector('[data-sst-scratch-toast]');
    if (existingToast) existingToast.remove();

    const toast = document.createElement('div');
    toast.className = 'sst-scratch-popup__toast';
    toast.setAttribute('data-sst-scratch-toast', '');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = this.toastMessage;
    document.body.appendChild(toast);

    window.requestAnimationFrame(() => toast.classList.add('is-visible'));
    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 280);
    }, Math.max(this.toastDuration - 350, 3000));
  }

  initCanvas() {
    if (!this.canvas || this.canvas.dataset.ready === 'true') return;

    this.canvas.dataset.ready = 'true';
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    this.paintScratchSurface();

    if (this.eiffelArt && !this.eiffelArt.complete) {
      this.eiffelArt.addEventListener('load', () => this.paintScratchSurface(), { once: true });
    }

    this.canvas.addEventListener('pointerdown', (event) => this.startScratch(event));
    this.canvas.addEventListener('pointermove', (event) => this.moveScratch(event));
    this.canvas.addEventListener('pointerup', () => this.endScratch());
    this.canvas.addEventListener('pointercancel', () => this.endScratch());
    this.canvas.addEventListener('pointerleave', () => this.endScratch());
  }

  paintScratchSurface() {
    const { width, height } = this.canvas;
    const ctx = this.ctx;

    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, width, height);

    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#d8c894');
    gradient.addColorStop(1, '#b99f5c');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    this.drawEiffelArt();

    ctx.fillStyle = 'rgba(255, 255, 255, 0.72)';
    for (let i = 0; i < 8; i += 1) {
      const leftSide = i % 2 === 0;
      const x = leftSide ? width * (0.1 + i * 0.025) : width * (0.78 + i * 0.012);
      const y = height * (0.25 + (i % 4) * 0.15);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(leftSide ? -0.22 : -0.28);
      ctx.fillRect(0, 0, width * 0.12, height * 0.045);
      ctx.restore();
    }

    ctx.fillStyle = 'rgba(74, 58, 52, 0.78)';
    ctx.font = '700 28px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('SCRATCH HERE', width / 2, height * 0.9);
  }

  drawEiffelArt() {
    if (!this.eiffelArt || !this.eiffelArt.complete || !this.eiffelArt.naturalWidth) return;

    const { width, height } = this.canvas;
    const artSize = Math.round(height * 0.72);
    const artX = (width - artSize) / 2;
    const artY = height * 0.08;
    const offscreen = document.createElement('canvas');
    const offscreenCtx = offscreen.getContext('2d', { willReadFrequently: true });

    offscreen.width = artSize;
    offscreen.height = artSize;
    offscreenCtx.drawImage(this.eiffelArt, 0, 0, artSize, artSize);

    const imageData = offscreenCtx.getImageData(0, 0, artSize, artSize);
    const pixels = imageData.data;

    for (let i = 0; i < pixels.length; i += 4) {
      const red = pixels[i];
      const green = pixels[i + 1];
      const blue = pixels[i + 2];

      if (red > 238 && green > 238 && blue > 238) {
        pixels[i + 3] = 0;
      } else {
        const alpha = pixels[i + 3];
        pixels[i] = 74;
        pixels[i + 1] = 58;
        pixels[i + 2] = 52;
        pixels[i + 3] = Math.min(alpha, 205);
      }
    }

    offscreenCtx.putImageData(imageData, 0, 0);
    this.ctx.drawImage(offscreen, artX, artY);
  }

  startScratch(event) {
    this.isScratching = true;
    this.canvas.setPointerCapture(event.pointerId);
    this.scratch(event);
  }

  moveScratch(event) {
    if (!this.isScratching) return;
    this.scratch(event);
  }

  endScratch() {
    if (!this.isScratching) return;
    this.isScratching = false;
    this.checkProgress();
  }

  scratch(event) {
    const point = this.getCanvasPoint(event);
    const radius = this.canvas.width * 0.085;

    this.ctx.globalCompositeOperation = 'destination-out';
    this.ctx.beginPath();
    this.ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
    this.ctx.fill();

    this.checkProgress();
  }

  getCanvasPoint(event) {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * this.canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * this.canvas.height
    };
  }

  checkProgress() {
    if (this.isRevealed) return;

    const pixels = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height).data;
    let transparent = 0;

    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] < 20) transparent += 1;
    }

    const percent = Math.round((transparent / (pixels.length / 4)) * 100);
    if (percent >= this.threshold) this.reveal();
  }

  reveal() {
    this.isRevealed = true;
    this.card?.classList.add('is-revealed');
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    if (this.progress) this.progress.hidden = true;
    window.setTimeout(() => this.showEmailForm(), 900);
  }

  async copyCode() {
    const code = this.discountCode?.textContent?.trim();
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      this.copyButton.textContent = this.canvas?.dataset.copiedLabel || 'Copied';
    } catch (error) {
      this.copyButton.textContent = code;
    }
  }
}

if (!customElements.get('sst-scratch-newsletter-popup')) {
  customElements.define('sst-scratch-newsletter-popup', SstScratchNewsletterPopup);
}
